import {
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Pencil,
  Plus,
  Search,
  Star,
  Table as TableIcon,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useEmployees } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import { useMembers } from '@/features/members';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { BASE as REVIEWS_BASE } from '../api';
import {
  REVIEW_STATUSES,
  reviewStatusLabel,
  reviewStatusVariant,
  type ReviewStatus,
} from '../constants';
import { useDeleteReview, useReviews } from '../queries';
import type { PerformanceReview } from '../types';
import { useReviewsUi, type ReviewsViewMode } from '../store/viewPrefs';

const ALL = 'all';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type ReviewOrderingColumn = 'period' | 'status' | 'rating' | 'review_date';

const VIEW_MODES: readonly ViewModeOption<ReviewsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

export function ReviewsListPage() {
  const navigate = useNavigate();
  const view = useReviewsUi((s) => s.view);
  const setView = useReviewsUi((s) => s.setView);
  const ordering = useReviewsUi((s) => s.ordering);
  const setOrdering = useReviewsUi((s) => s.setOrdering);

  // Server-driven filters live in local state — pagination + search hit the API.
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [statusFilter, setStatusFilter] = useState<ReviewStatus | typeof ALL>(ALL);
  const [page, setPage] = useState(1);

  const [toDelete, setToDelete] = useState<PerformanceReview | null>(null);

  const { data, isPending, isError, error, isFetching } = useReviews({
    page,
    search: search || undefined,
    status: statusFilter === ALL ? undefined : statusFilter,
    ordering,
  });
  const { data: employeesData } = useEmployees({});
  const { data: membersData } = useMembers();

  const employeeLabels = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of employeesData?.results ?? []) map.set(e.id, employeeName(e));
    return map;
  }, [employeesData]);
  const reviewerNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of membersData?.results ?? []) map.set(m.user.id, m.user.full_name);
    return map;
  }, [membersData]);

  const remove = useDeleteReview();

  const reviews = data?.results ?? [];

  const employeeLabel = (r: PerformanceReview) =>
    employeeLabels.get(r.employee) ?? `#${r.employee}`;
  const reviewerLabel = (r: PerformanceReview) =>
    r.reviewer ? (reviewerNames.get(r.reviewer) ?? '…') : '—';

  const open = (r: PerformanceReview) => navigate(`/hrm/reviews/${r.public_id}`);
  const openEdit = (r: PerformanceReview) => navigate(`/hrm/reviews/${r.public_id}/edit`);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Performance Reviews</h1>
        <Button onClick={() => navigate('/hrm/reviews/new')}>
          <Plus className="size-4" /> New review
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by employee or period…"
              className="pl-8"
              aria-label="Search reviews"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setPage(1);
              setStatusFilter(v as ReviewStatus | typeof ALL);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {REVIEW_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `reviews.csv` without leaving the SPA.
              void exportCsv(REVIEWS_BASE, {
                search: search || undefined,
                status: statusFilter === ALL ? undefined : statusFilter,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export reviews as CSV"
            title="Export reviews as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load reviews.'}
        </p>
      ) : isPending ? (
        <ReviewsSkeleton />
      ) : view === 'table' ? (
        <ReviewsTable
          reviews={reviews}
          employeeLabel={employeeLabel}
          reviewerLabel={reviewerLabel}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onOpen={open}
          onEdit={openEdit}
          onDelete={setToDelete}
        />
      ) : view === 'list' ? (
        <ReviewsList reviews={reviews} employeeLabel={employeeLabel} onOpen={open} />
      ) : view === 'cards' ? (
        <ReviewsCards
          reviews={reviews}
          employeeLabel={employeeLabel}
          reviewerLabel={reviewerLabel}
          onOpen={open}
        />
      ) : (
        <ReviewsGroupedByStatus
          reviews={reviews}
          employeeLabel={employeeLabel}
          reviewerLabel={reviewerLabel}
          onOpen={open}
        />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'review' : 'reviews'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete review?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---- Shared helpers --------------------------------------------------------

function RatingValue({ rating }: { rating: number | null }) {
  if (rating == null) return <>—</>;
  return (
    <span className="flex items-center gap-1">
      <Star className="size-4 fill-current text-amber-500" />
      {rating}
      <span className="text-muted-foreground">/5</span>
    </span>
  );
}

function StatusBadge({ status }: { status: ReviewStatus }) {
  return <Badge variant={reviewStatusVariant(status)}>{reviewStatusLabel(status)}</Badge>;
}

// ---- View renderers --------------------------------------------------------

interface BaseViewProps {
  reviews: PerformanceReview[];
  employeeLabel: (r: PerformanceReview) => string;
  onOpen: (r: PerformanceReview) => void;
}

interface ReviewsTableProps {
  reviews: PerformanceReview[];
  employeeLabel: (r: PerformanceReview) => string;
  reviewerLabel: (r: PerformanceReview) => string;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  onOpen: (r: PerformanceReview) => void;
  onEdit: (r: PerformanceReview) => void;
  onDelete: (r: PerformanceReview) => void;
}

function ReviewsTable({
  reviews,
  employeeLabel,
  reviewerLabel,
  ordering,
  onOrderingChange,
  onOpen,
  onEdit,
  onDelete,
}: ReviewsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Employee</TableHead>
            <SortableTableHead<ReviewOrderingColumn>
              column="period"
              value={ordering}
              onChange={onOrderingChange}
            >
              Period
            </SortableTableHead>
            <TableHead className="hidden md:table-cell">Reviewer</TableHead>
            <SortableTableHead<ReviewOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<ReviewOrderingColumn>
              column="rating"
              value={ordering}
              onChange={onOrderingChange}
            >
              Rating
            </SortableTableHead>
            <SortableTableHead<ReviewOrderingColumn>
              column="review_date"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden lg:table-cell"
            >
              Review date
            </SortableTableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reviews.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-muted-foreground py-10 text-center">
                No reviews found.
              </TableCell>
            </TableRow>
          ) : (
            reviews.map((r) => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => onOpen(r)}>
                <TableCell className="font-medium">{employeeLabel(r)}</TableCell>
                <TableCell>{r.period || '—'}</TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {reviewerLabel(r)}
                </TableCell>
                <TableCell>
                  <StatusBadge status={r.status} />
                </TableCell>
                <TableCell>
                  <RatingValue rating={r.rating} />
                </TableCell>
                <TableCell className="text-muted-foreground hidden whitespace-nowrap lg:table-cell">
                  {formatDate(r.review_date)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Edit review"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(r);
                    }}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Delete review"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(r);
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

interface ReviewsCardsProps extends BaseViewProps {
  reviewerLabel: (r: PerformanceReview) => string;
}

function ReviewsCards({ reviews, employeeLabel, reviewerLabel, onOpen }: ReviewsCardsProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {reviews.map((r) => (
        <Card
          key={r.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(r)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(r);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{employeeLabel(r)}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <StatusBadge status={r.status} />
            {r.period ? <span className="text-muted-foreground truncate">{r.period}</span> : null}
            <span className="text-muted-foreground truncate">{reviewerLabel(r)}</span>
            <span className="ml-auto">
              <RatingValue rating={r.rating} />
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ReviewsList({ reviews, employeeLabel, onOpen }: BaseViewProps) {
  return (
    <ul className="divide-y rounded-md border">
      {reviews.map((r) => (
        <li key={r.id}>
          {/* Use a real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => onOpen(r)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">
              {employeeLabel(r)}
            </span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {r.period || '—'}
            </span>
            <StatusBadge status={r.status} />
            <span className="text-muted-foreground text-xs">{formatDate(r.review_date)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

interface ReviewsGroupedProps extends BaseViewProps {
  reviewerLabel: (r: PerformanceReview) => string;
}

function ReviewsGroupedByStatus({
  reviews,
  employeeLabel,
  reviewerLabel,
  onOpen,
}: ReviewsGroupedProps) {
  // Bucket by status, preserving the declared status order; skip empty groups.
  const groups = useMemo(() => {
    return REVIEW_STATUSES.map((s) => ({
      key: s.value,
      label: s.label,
      bucket: reviews.filter((r) => r.status === s.value),
    }));
  }, [reviews]);

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`reviews-status-${key}`}>
            <h2
              id={`reviews-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <ReviewsCards
              reviews={bucket}
              employeeLabel={employeeLabel}
              reviewerLabel={reviewerLabel}
              onOpen={onOpen}
            />
          </section>
        );
      })}
    </div>
  );
}

function ReviewsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
